import { useEffect, useRef, type ComponentType } from 'react'
import type { BusinessHoursRow, BusinessLocation } from '@webbuilder/schemas'
import { IconBuildingStore, IconClock } from '@tabler/icons-react'
import { IconAlertTriangle } from '@tabler/icons-react'
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTiktok,
  IconBrandWhatsapp,
  IconBrandX,
  IconBrandYoutube,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPlus,
  IconTrash,
  IconWorld,
} from '@tabler/icons-react'
import { CONTACT_KINDS, isValidEmail, type ContactEntry, type ContactKind } from '../../lib/preview/compose'
import { useI18n } from '../../lib/i18n'
import { PhoneInput } from './PhoneInput'

/**
 * El contacto y las redes, sin campos fijos.
 *
 * Antes eran cinco casillas seguidas —correo, teléfono, dirección, Instagram y
 * Facebook— y quien no tenía Facebook veía una pregunta que no iba con él, y
 * quien tenía LinkedIn o WhatsApp no tenía dónde ponerlo. Ahora se añade lo que
 * se tiene, de una lista, y cada cosa añadida sale en el pie del sitio.
 */

type KindMeta = { Icon: ComponentType<{ size?: number }>; group: 'contact' | 'social' }

const KINDS: Record<ContactKind, KindMeta> = {
  email: { Icon: IconMail, group: 'contact' },
  phone: { Icon: IconPhone, group: 'contact' },
  whatsapp: { Icon: IconBrandWhatsapp, group: 'contact' },
  address: { Icon: IconMapPin, group: 'contact' },
  instagram: { Icon: IconBrandInstagram, group: 'social' },
  facebook: { Icon: IconBrandFacebook, group: 'social' },
  linkedin: { Icon: IconBrandLinkedin, group: 'social' },
  tiktok: { Icon: IconBrandTiktok, group: 'social' },
  x: { Icon: IconBrandX, group: 'social' },
  youtube: { Icon: IconBrandYoutube, group: 'social' },
  website: { Icon: IconWorld, group: 'social' },
}

export function WizardContactStep({ contacts, onChange, missing = [], mapNeedsAddress = false, hours = [], onHoursChange, pageShowsHours = false, locations = [], onLocationsChange }: {
  contacts: ContactEntry[]
  onChange: (next: ContactEntry[]) => void
  /** El horario del negocio (ver `businessHours.ts` en schemas). */
  hours?: BusinessHoursRow[]
  onHoursChange?: (next: BusinessHoursRow[]) => void
  /** La página enseña un horario: sin el suyo, saldría el de ejemplo. */
  pageShowsHours?: boolean
  /**
   * Las sucursales, sólo con dos o más (2026-10-30). Con una, la dirección de
   * contacto de arriba basta. La IA las trae si el pedido nombraba varias.
   */
  locations?: BusinessLocation[]
  onLocationsChange?: (next: BusinessLocation[]) => void
  /** Hay un mapa sin dirección: la dirección de aquí es la que enseña. */
  mapNeedsAddress?: boolean
  /**
   * Los datos que la página enseña y aún no tienen valor (`missingContactKinds`):
   * sin ellos el sitio sale con «Agrega tu dirección».
   */
  missing?: readonly string[]
}) {
  const { t } = useI18n()
  const lastAdded = useRef<string | null>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const addedMissing = useRef(false)

  /* Al entrar, los que pide la página ya vienen puestos, vacíos y marcados:
     así se ve qué falta sin tener que adivinarlo por la vista previa. */
  useEffect(() => {
    if (addedMissing.current) return
    addedMissing.current = true
    const absent = missing.filter((kind): kind is ContactKind => (CONTACT_KINDS as readonly string[]).includes(kind) && !contacts.some((entry) => entry.kind === kind))
    if (absent.length) onChange([...contacts, ...absent.map((kind) => ({ id: `${kind}-needed`, kind, value: '' }))])
  }, [contacts, missing, onChange])

  /* El campo recién añadido recibe el foco: se pulsa «Instagram» para escribirlo. */
  useEffect(() => {
    if (!lastAdded.current) return
    listRef.current?.querySelector<HTMLInputElement>(`[data-entry="${lastAdded.current}"] input`)?.focus()
    lastAdded.current = null
  }, [contacts])

  const add = (kind: ContactKind) => {
    const id = `${kind}-${Date.now().toString(36)}`
    lastAdded.current = id
    onChange([...contacts, { id, kind, value: '' }])
  }
  const update = (id: string, value: string) => onChange(contacts.map((entry) => (entry.id === id ? { ...entry, value } : entry)))
  const remove = (id: string) => onChange(contacts.filter((entry) => entry.id !== id))

  const used = new Set(contacts.map((entry) => entry.kind))
  const available = CONTACT_KINDS.filter((kind) => !used.has(kind))

  return (
    <>
      <div className="dash-svc-setting-block cwa-wz-block">
        <label>{t('cwa.contact.added')}</label>
        <span className="dash-svc-block-hint">{t('cwa.contact.addedHint')}</span>
        {missing.length ? (
          <p className="cwa-wz-contacts-needed" role="status">
            <IconAlertTriangle size={15} aria-hidden="true" />
            <span>{t('cwa.contact.needed')}</span>
          </p>
        ) : null}
        {contacts.length ? (
          <ul className="cwa-wz-contacts" ref={listRef}>
            {contacts.map((entry) => {
              const { Icon } = KINDS[entry.kind]
              const invalid = entry.kind === 'email' && entry.value.trim() !== '' && !isValidEmail(entry.value)
              /* Vacío = por completar, lo pida la página o no; y si lo pide,
                 se dice dónde (el mapa, o la página en general). */
              const needed = !entry.value.trim()
              const neededTag = entry.kind === 'address' && mapNeedsAddress ? t('cwa.contact.neededMap') : missing.includes(entry.kind) ? t('cwa.contact.neededTag') : t('aiPending.chip')
              return (
                <li key={entry.id} data-entry={entry.id} className={invalid ? 'is-invalid' : needed ? 'is-needed' : ''}>
                  <span className="cwa-wz-contact-icon" aria-hidden="true"><Icon size={16} /></span>
                  <span className="cwa-wz-contact-field">
                    <small>{t(`cwa.contact.kind.${entry.kind}`)}</small>
                    {entry.kind === 'phone' || entry.kind === 'whatsapp' ? (
                      <PhoneInput value={entry.value} onChange={(value) => update(entry.id, value)} />
                    ) : (
                      <input
                        type={entry.kind === 'email' ? 'email' : 'text'}
                        value={entry.value}
                        placeholder={t(`cwa.contact.placeholder.${entry.kind}`)}
                        aria-label={t(`cwa.contact.kind.${entry.kind}`)}
                        aria-invalid={invalid || undefined}
                        onChange={(event) => update(entry.id, event.target.value)}
                      />
                    )}
                    {invalid ? <em>{t('cwa.contactEmailInvalid')}</em> : needed ? <em className="cwa-wz-contact-needed">{neededTag}</em> : null}
                  </span>
                  <button type="button" className="dash-icon-btn cwa-wz-contact-remove" aria-label={t('cwa.contact.remove')} onClick={() => remove(entry.id)}>
                    <IconTrash size={14} />
                  </button>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="cwa-wz-contacts-empty">{t('cwa.contact.empty')}</p>
        )}
      </div>

      {onHoursChange ? (
        <div className="dash-svc-setting-block cwa-wz-block">
          <label>{t('cwa.hours.title')}</label>
          <span className="dash-svc-block-hint">{t('cwa.hours.hint')}</span>
          {pageShowsHours && !hours.length ? (
            <p className="cwa-wz-contacts-needed" role="status">
              <IconAlertTriangle size={15} aria-hidden="true" />
              <span>{t('cwa.hours.needed')}</span>
            </p>
          ) : null}
          {hours.length ? (
            <ul className="cwa-wz-contacts cwa-wz-hours">
              {hours.map((row, index) => (
                <li key={index} className={!row.days.trim() || !row.time.trim() ? 'is-needed' : ''}>
                  <span className="cwa-wz-contact-icon" aria-hidden="true"><IconClock size={16} /></span>
                  <span className="cwa-wz-contact-field">
                    <small>{t('cwa.hours.days')}</small>
                    <input value={row.days} placeholder={t('cwa.hours.daysPlaceholder')} aria-label={t('cwa.hours.days')} onChange={(event) => onHoursChange(hours.map((item, at) => (at === index ? { ...item, days: event.target.value } : item)))} />
                  </span>
                  <span className="cwa-wz-contact-field">
                    <small>{t('cwa.hours.time')}</small>
                    <input value={row.time} placeholder={t('cwa.hours.timePlaceholder')} aria-label={t('cwa.hours.time')} onChange={(event) => onHoursChange(hours.map((item, at) => (at === index ? { ...item, time: event.target.value } : item)))} />
                  </span>
                  <button type="button" className="dash-icon-btn cwa-wz-contact-remove" aria-label={t('cwa.contact.remove')} onClick={() => onHoursChange(hours.filter((_, at) => at !== index))}>
                    <IconTrash size={14} />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {hours.length < 7 ? (
            <div className="cwa-wz-add">
              <button type="button" onClick={() => onHoursChange([...hours, { days: '', time: '' }])}>
                <IconClock size={15} />
                <span>{t('cwa.hours.add')}</span>
                <IconPlus size={13} className="cwa-wz-add-plus" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {onLocationsChange ? (
        <div className="dash-svc-setting-block cwa-wz-block">
          <label>{t('cwa.locations.title')}</label>
          <span className="dash-svc-block-hint">{t('cwa.locations.hint')}</span>
          {locations.length ? (
            <ul className="cwa-wz-contacts cwa-wz-hours">
              {locations.map((location, index) => {
                const set = (patch: Partial<BusinessLocation>) => onLocationsChange(locations.map((item, at) => (at === index ? { ...item, ...patch } : item)))
                return (
                  <li key={index} className={location.address.trim() ? '' : 'is-needed'}>
                    <span className="cwa-wz-contact-icon" aria-hidden="true"><IconBuildingStore size={16} /></span>
                    <span className="cwa-wz-contact-field">
                      <small>{t('cwa.locations.name')}</small>
                      <input value={location.name} placeholder={t('cwa.locations.namePlaceholder')} aria-label={t('cwa.locations.name')} onChange={(event) => set({ name: event.target.value })} />
                    </span>
                    <span className="cwa-wz-contact-field">
                      <small>{t('cwa.locations.address')}</small>
                      <input value={location.address} placeholder={t('cwa.locations.addressPlaceholder')} aria-label={t('cwa.locations.address')} onChange={(event) => set({ address: event.target.value })} />
                    </span>
                    <span className="cwa-wz-contact-field">
                      <small>{t('cwa.locations.phone')}</small>
                      <input value={location.phone ?? ''} inputMode="tel" aria-label={t('cwa.locations.phone')} onChange={(event) => set({ phone: event.target.value })} />
                    </span>
                    <span className="cwa-wz-contact-field">
                      <small>{t('cwa.locations.hours')}</small>
                      <input value={location.hours ?? ''} placeholder={t('cwa.hours.timePlaceholder')} aria-label={t('cwa.locations.hours')} onChange={(event) => set({ hours: event.target.value })} />
                    </span>
                    {/* Con una sola, no son sucursales: la dirección de arriba basta. */}
                    <button type="button" className="dash-icon-btn cwa-wz-contact-remove" aria-label={t('cwa.contact.remove')} onClick={() => { const next = locations.filter((_, at) => at !== index); onLocationsChange(next.length > 1 ? next : []) }}>
                      <IconTrash size={14} />
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : null}
          {locations.length < 8 ? (
            <div className="cwa-wz-add">
              <button
                type="button"
                onClick={() => {
                  /* La primera vez, la dirección de contacto pasa a ser la primera sucursal. */
                  const main = contacts.find((entry) => entry.kind === 'address' && entry.value.trim())?.value.trim() ?? ''
                  onLocationsChange(locations.length ? [...locations, { name: '', address: '' }] : [{ name: '', address: main }, { name: '', address: '' }])
                }}
              >
                <IconBuildingStore size={15} />
                <span>{t('cwa.locations.add')}</span>
                <IconPlus size={13} className="cwa-wz-add-plus" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {available.length ? (
        <div className="dash-svc-setting-block cwa-wz-block">
          <label>{t('cwa.contact.add')}</label>
          {(['contact', 'social'] as const).map((group) => {
            const kinds = available.filter((kind) => KINDS[kind].group === group)
            if (!kinds.length) return null
            return (
              <div key={group} className="cwa-wz-add-group">
                <small>{t(`cwa.contact.group.${group}`)}</small>
                <div className="cwa-wz-add">
                  {kinds.map((kind) => {
                    const { Icon } = KINDS[kind]
                    return (
                      <button key={kind} type="button" onClick={() => add(kind)}>
                        <Icon size={15} />
                        <span>{t(`cwa.contact.kind.${kind}`)}</span>
                        <IconPlus size={13} className="cwa-wz-add-plus" />
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </>
  )
}
